import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, X, Bell } from 'lucide-react';
import { siteInfo } from '../data/mockData';
import { useContent } from '../context/ContentContext';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import NotificationsPanel from './NotificationsPanel';

const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const { profile } = useAuth();
  const { posts: allPosts } = useContent();

  const unseenCount = (profile?.notifications || []).filter((n) => !n.seen).length;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setNotifOpen(false);
  }, [location]);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const filtered = allPosts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 5));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, allPosts]);

  return (
    <header
      className={`fixed top-0 z-50 transition-all duration-300 w-full max-w-[640px] left-1/2 -translate-x-1/2 ${
        scrolled
          ? 'glass shadow-lg border-b border-[#D6AD60]/20'
          : 'bg-[#F7F3EA]'
      }`}
    >
      <div className="flex items-center justify-between px-5 h-[72px]">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={44} className="rounded-full shadow-md ring-1 ring-[#D6AD60]/40" />
          <div>
            <h1
              className="text-base font-semibold text-stone-800 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Reflections
            </h1>
            <p className="text-[10px] text-[#D6AD60] tracking-wider uppercase">
              by Rowdy
            </p>
            {/* Gold filigree accent — echoes the logo's inner ring */}
            <span
              aria-hidden="true"
              className="flex items-center gap-1 mt-1"
              data-testid="header-filigree"
            >
              <span className="block h-px w-5 bg-gradient-to-r from-transparent via-[#D6AD60]/70 to-[#D6AD60]" />
              <span className="block w-1 h-1 rotate-45 bg-[#D6AD60] shadow-[0_0_6px_rgba(218,165,32,0.7)]" />
              <span className="block h-px w-5 bg-gradient-to-l from-transparent via-[#D6AD60]/70 to-[#D6AD60]" />
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {profile && (
            <button
              onClick={() => setNotifOpen(true)}
              className="relative w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-stone-200 flex items-center justify-center transition-colors duration-300"
              data-testid="header-notif-btn"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-[#D6AD60]" />
              {unseenCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center shadow-sm"
                  data-testid="header-notif-badge"
                >
                  {unseenCount > 9 ? '9+' : unseenCount}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-stone-200 flex items-center justify-center transition-colors duration-300"
          >
            {searchOpen ? (
              <X className="w-5 h-5 text-[#D6AD60]" />
            ) : (
              <Search className="w-5 h-5 text-[#D6AD60]" />
            )}
          </button>
        </div>
      </div>

      {/* Search Panel */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          searchOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D6AD60]/70" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reflections..."
              className="w-full pl-11 pr-4 py-3 bg-[#FFFCF3] border border-[#D6AD60]/20 rounded-2xl text-sm text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40 shadow-inner"
              autoFocus={searchOpen}
            />
          </div>

          {searchResults.length > 0 && (
            <div className="mt-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl shadow-lg overflow-hidden">
              {searchResults.map((post) => (
                <Link
                  key={post.id}
                  to={`/post/${post.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors duration-200 border-b border-stone-100 last:border-0"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">
                      {post.title}
                    </p>
                    <p className="text-xs text-[#D6AD60]/80 capitalize">
                      {post.category.replace('-', ' ')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {searchQuery.trim().length > 1 && searchResults.length === 0 && (
            <div className="mt-3 text-center py-4">
              <p className="text-sm text-stone-500">No results found</p>
            </div>
          )}

          {/* Deep search — routes to the universal index for affirmations,
              manifest themes, and everything else the header quick-search
              doesn't cover. */}
          <div className="mt-2 text-center">
            <Link
              to={`/search${searchQuery.trim() ? `?q=${encodeURIComponent(searchQuery.trim())}` : ''}`}
              onClick={() => setSearchOpen(false)}
              data-testid="header-full-search-link"
              className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#D6AD60] hover:text-[#B8934D] transition-colors"
            >
              Search everything →
            </Link>
          </div>
        </div>
      </div>
      <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
    </header>
  );
};

export default Header;
